import { z } from 'zod';
import { PERMISSIONS, type Permission } from './permissions.js';

/**
 * Abonnements (cahier des charges §14). Paiements saisis à la main par l'administrateur général :
 * aucun prélèvement automatique, aucune offre ni aucun tarif fixé par l'application.
 */
export const SUBSCRIPTION_STATUSES = ['TRIAL', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CANCELLED'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  TRIAL: "À l'essai",
  ACTIVE: 'Actif',
  PAST_DUE: 'Échéance dépassée',
  SUSPENDED: 'Suspendu (lecture seule)',
  CANCELLED: 'Résilié (lecture seule)',
};

/** Ces statuts bloquent les écritures ; la consultation et les exports restent ouverts, aucune donnée n'est retirée. */
export const READ_ONLY_STATUSES: readonly SubscriptionStatus[] = ['SUSPENDED', 'CANCELLED'];

const limit = z.number().int().positive().nullable();
const amount = z.string().regex(/^\d{1,13}(\.\d{1,2})?$/, { error: 'Montant invalide' });
const currency = z.string().trim().length(3).toUpperCase();

export const planCreateSchema = z.object({
  code: z.string().trim().min(2).max(30).regex(/^[A-Z0-9_]+$/, { error: 'Code en majuscules, chiffres et _' }),
  name: z.string().trim().min(2).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  /** Vide = sans limite. */
  maxUsers: limit.optional(),
  maxProjects: limit.optional(),
  aiMonthlyTokens: limit.optional(),
  /** Tarif fixé par l'administrateur général, dans la devise de son choix. Vide = à définir. */
  priceAmount: amount.nullable().optional(),
  priceCurrency: currency.nullable().optional(),
  periodMonths: z.number().int().min(1).max(60).default(1),
  /** Prix d'un téléchargement de document. Vide ou 0 : téléchargements inclus. */
  downloadPrice: amount.nullable().optional(),
});
export const planUpdateSchema = planCreateSchema.omit({ code: true }).partial().extend({ version: z.number().int().positive(), isActive: z.boolean().optional() });

export const subscriptionUpdateSchema = z.object({
  version: z.number().int().positive(),
  planId: z.uuid().optional(),
  status: z.enum(SUBSCRIPTION_STATUSES).optional(),
  currentPeriodEnd: z.iso.date().nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});

export const subscriptionPaymentSchema = z.object({
  amount: amount.refine((v) => Number(v) > 0, { error: 'Le montant doit être positif' }),
  currency,
  paidOn: z.iso.date(),
  method: z.string().trim().min(2).max(60),
  reference: z.string().trim().max(120).nullable().optional(),
  /** Nouvelle échéance : prolonge l'abonnement et le remet en statut « actif ». */
  periodEnd: z.iso.date().nullable().optional(),
});
export const subscriptionPaymentCancelSchema = z.object({ reason: z.string().trim().min(3).max(300) });

export const downloadCreditsSchema = z.object({
  /** Crédits à ajouter ; négatif pour corriger une erreur. */
  add: z.number().int().min(-10000).max(10000).refine((v) => v !== 0, { error: 'Indiquez un nombre de crédits' }),
});

export const platformUserUpdateSchema = z.object({ status: z.enum(['ACTIVE', 'DISABLED']) });

/** Rôle propre à une entreprise : regroupement libre des permissions. */
const permissionCode = z.string().refine((p): p is Permission => p in PERMISSIONS, { error: 'Permission inconnue' });
export const roleCreateSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(300).nullable().optional(),
  permissions: z.array(permissionCode).min(1),
});
export const roleUpdateSchema = roleCreateSchema.partial();

/**
 * Formules de départ proposées à l'administrateur général : elles ne s'ajoutent qu'à sa demande,
 * et tout y reste modifiable. Les tarifs sont des propositions commerciales, pas des prix de marché.
 */
export const PROPOSED_PLANS = [
  { code: 'DECOUVERTE', name: 'Découverte', description: 'Pour essayer ExpertDevis BTP sur vos premiers devis.', maxUsers: 1, maxProjects: 3, aiMonthlyTokens: 100_000, priceAmount: '0', priceCurrency: 'XAF', periodMonths: 1 },
  { code: 'ARTISAN', name: 'Artisan', description: 'Pour un artisan ou un petit entrepreneur.', maxUsers: 2, maxProjects: 15, aiMonthlyTokens: 300_000, priceAmount: '15000', priceCurrency: 'XAF', periodMonths: 1 },
  { code: 'PME', name: 'PME', description: "Pour une entreprise avec une petite équipe : études, devis et factures.", maxUsers: 5, maxProjects: 60, aiMonthlyTokens: 1_000_000, priceAmount: '35000', priceCurrency: 'XAF', periodMonths: 1 },
  { code: 'ENTREPRISE', name: 'Entreprise', description: "Pour un bureau d'études ou une grande équipe.", maxUsers: 15, maxProjects: null, aiMonthlyTokens: 3_000_000, priceAmount: '75000', priceCurrency: 'XAF', periodMonths: 1 },
] as const;
